import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["activeStepIndex", "children"],
    _excluded2 = ["activeStepIndex", "className", "stepSeparator", "id", "isControlled", "mode", "size", "actions", "children", "chip", "customStepCount", "defaultStepIndex", "isOpen", "title", "onCancel", "onSubmit", "submitButtonLabel", "cancelButtonLabel", "onClosed", "onOpened"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { forwardRef, useState, useEffect, createContext, useRef, Children, cloneElement, isValidElement } from 'react';
import { ThemeProvider } from 'styled-components';
import cx from 'classnames';
import { prefix } from '../../tools/settings';
import { Styled } from './Wizard.styles';
import Header from './components/Header';
import Step from './components/Step';
import Footer from './components/Footer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var WizardContext = createContext();
var ScrollBody = forwardRef(function (_ref, ref) {
  var activeStepIndex = _ref.activeStepIndex,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded);

  ref = useRef();
  useEffect(function () {
    ref.current.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [activeStepIndex, ref]);
  return _jsx(Styled.ScrollBody, _objectSpread(_objectSpread({
    ref: ref
  }, rest), {}, {
    children: children
  }));
});
var Wizard = forwardRef(function (_ref2, ref) {
  var _cx;

  var _ref2$activeStepIndex = _ref2.activeStepIndex,
      activeStepIndexProp = _ref2$activeStepIndex === void 0 ? function () {
    return null;
  } : _ref2$activeStepIndex,
      customClassName = _ref2.className,
      _ref2$stepSeparator = _ref2.stepSeparator,
      stepSeparator = _ref2$stepSeparator === void 0 ? 'of' : _ref2$stepSeparator,
      id = _ref2.id,
      _ref2$isControlled = _ref2.isControlled,
      isControlled = _ref2$isControlled === void 0 ? false : _ref2$isControlled,
      _ref2$mode = _ref2.mode,
      mode = _ref2$mode === void 0 ? 'light' : _ref2$mode,
      _ref2$size = _ref2.size,
      size = _ref2$size === void 0 ? 'medium' : _ref2$size,
      actions = _ref2.actions,
      children = _ref2.children,
      chip = _ref2.chip,
      customStepCount = _ref2.customStepCount,
      _ref2$defaultStepInde = _ref2.defaultStepIndex,
      defaultStepIndex = _ref2$defaultStepInde === void 0 ? 0 : _ref2$defaultStepInde,
      isOpen = _ref2.isOpen,
      title = _ref2.title,
      legacyOnCancel = _ref2.onCancel,
      legacyOnSubmit = _ref2.onSubmit,
      legacySubmitButtonLabel = _ref2.submitButtonLabel,
      legacyCancelButtonLabel = _ref2.cancelButtonLabel,
      _onClosed = _ref2.onClosed,
      _onOpened = _ref2.onOpened,
      rest = _objectWithoutProperties(_ref2, _excluded2);

  var className = cx((_cx = {}, _defineProperty(_cx, "".concat(prefix, "-wizard"), true), _defineProperty(_cx, customClassName, !!customClassName), _cx));

  var _useState = useState(!isOpen),
      _useState2 = _slicedToArray(_useState, 2),
      isClosed = _useState2[0],
      setIsClosed = _useState2[1];

  var _useState3 = useState(defaultStepIndex),
      _useState4 = _slicedToArray(_useState3, 2),
      activeStepIndex = _useState4[0],
      setActiveStepIndex = _useState4[1];

  useEffect(function () {
    if (isControlled || isClosed) {
      setActiveStepIndex(defaultStepIndex);
    }
  }, [defaultStepIndex, isClosed, isControlled]);
  var clones = Children.map(children, function (child, index) {
    if (!isValidElement(child)) return;
    return cloneElement(child, {
      stepIndex: index
    });
  });
  var stepCount = Children.count(children);
  var activeStepProps = clones[activeStepIndex].props;
  var isValid = activeStepProps.isValid;
  var prevStepIndex = activeStepIndex - 1;
  var nextStepIndex = activeStepIndex + 1;
  var nextStepTitle = nextStepIndex < stepCount ? clones[nextStepIndex].props.title : null;
  var prevStepTitle = prevStepIndex >= 0 ? clones[prevStepIndex].props.title : null;

  var onNextHandler = function onNextHandler() {
    setActiveStepIndex(nextStepIndex);
  };

  var onPrevHandler = function onPrevHandler() {
    setActiveStepIndex(prevStepIndex);
  };

  useEffect(function () {
    if (typeof window.platformChrome === 'function') {
      if (isOpen) {
        window.platformChrome('hide');
      } else {
        window.platformChrome('show');
      }
    }
  }, [isOpen]);
  useEffect(function () {
    activeStepIndexProp(activeStepIndex);
  }, [activeStepIndex, activeStepIndexProp]);
  return _jsx(ThemeProvider, {
    theme: {
      mode: mode,
      size: size
    },
    children: _jsx(WizardContext.Provider, {
      value: {
        activeStepIndex: activeStepIndex,
        activeStepProps: activeStepProps,
        chip: chip,
        defaultStepIndex: defaultStepIndex,
        stepSeparator: stepSeparator,
        customStepCount: customStepCount,
        customStepIndex: activeStepProps.customStepIndex,
        headerActions: actions,
        footerActions: activeStepProps.actions,
        isControlled: isControlled,
        isValid: isValid !== undefined ? isValid : true,
        legacyOnCancel: legacyOnCancel,
        legacyOnSubmit: legacyOnSubmit,
        legacySubmitButtonLabel: legacySubmitButtonLabel,
        legacyCancelButtonLabel: legacyCancelButtonLabel,
        nextStepTitle: nextStepTitle,
        onNextHandler: onNextHandler,
        onPrevHandler: onPrevHandler,
        prevStepTitle: prevStepTitle,
        stepCount: stepCount,
        title: title
      },
      children: _jsx(Styled.Overlay, _objectSpread(_objectSpread({
        ref: ref,
        id: id,
        className: className,
        hasBackdrop: false,
        isOpen: isOpen,
        onClosed: function onClosed() {
          setIsClosed(true);
          _onClosed && _onClosed();
        },
        onOpened: function onOpened() {
          setIsClosed(false);
          _onOpened && _onOpened();
        }
      }, rest), {}, {
        children: _jsxs(Styled.Wizard, {
          children: [_jsx(Header, {}), _jsx(ScrollBody, {
            activeStepIndex: activeStepIndex,
            tabIndex: "0",
            children: clones
          }), _jsx(Footer, {})]
        })
      }))
    })
  });
});
Wizard.Step = Step;
export default Wizard;